import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Clock, CheckCircle, XCircle, Search, Eye, Download } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/auth/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30';
      case 'processing': return 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-[#F97316]/20 text-[#F97316] border-[#F97316]/30';
    }
  };

  const filteredOrders = orders.filter(order => 
    filter === 'all' || order.status === filter
  );

  if (loading) {
    return (
      <DashboardLayout currentPage="/dashboard/orders">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="/dashboard/orders">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Orders</h1>
        <p className="text-white/50">View and manage your order history</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
        {['all', 'pending', 'processing', 'completed', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all capitalize whitespace-nowrap ${
              filter === status
                ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white'
                : 'bg-[#12121A] border border-white/10 text-white/60 hover:text-white'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <div className="bg-[#12121A] border border-white/10 rounded-2xl p-12 text-center">
          <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShoppingBag size={40} className="text-white/20" />
          </div>
          <h3 className="text-white font-semibold mb-2">No orders found</h3>
          <p className="text-white/50 mb-6">Start shopping to see your orders here</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 font-semibold text-white rounded-xl transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
          >
            Browse Services
          </button>
        </div>
      ) : (
        <div className="bg-[#12121A] border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/50 text-sm font-medium px-6 py-4">Order</th>
                  <th className="text-left text-white/50 text-sm font-medium px-6 py-4">Date</th>
                  <th className="text-left text-white/50 text-sm font-medium px-6 py-4">Items</th>
                  <th className="text-left text-white/50 text-sm font-medium px-6 py-4">Total</th>
                  <th className="text-left text-white/50 text-sm font-medium px-6 py-4">Status</th>
                  <th className="text-left text-white/50 text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">{order.order_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white/60 text-sm">
                        {new Date(order.timestamp).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white/60 text-sm">
                        {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">${order.total?.toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-lg text-xs font-semibold border capitalize ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => navigate(`/dashboard/projects/${order.id}`)}
                        className="text-[#3B82F6] hover:text-[#60A5FA] transition-colors"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Orders;
